/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useRef, useState } from 'react';
import { Background } from 'components/Background/Background';
import { IS_CHAT_BOT_ENABLED, IMAGES_URL } from '../../constants';
import Content from 'services/Content';
import { StyledHeaderContainer, StyledSmallLogo, StyledHomeMessageContainer, StyledWelcomeMessage, StyledWelcomeHeading, StyledChapterContainer, StyledChapters, StyledChapterBlockContainer, StyledDashboardBase, StyledDashboardContainer, StyledFullBlockContainer } from './PlayerDashboard.styled';
import { ChapterBlock } from './ChapterBlock';
import { UrgentHelpBlock } from './UrgentHelpBlock';
import { StyledChapterEntry, StyledChapterEntryIcon, StyledChapterEntrySupTitle, StyledChapterEntryTitle, StyledChapterEntryText, StyledChapterEntryButton, StyledChapterEntryBackground, StyledEntryTextContainer, StyledChapterEntryButtonContainer } from './PlayerDashboardEntry.styled';
import { IChapter } from 'models/IChapter';
import { awaitAnimationFrame } from 'utils/dom';
import { StyledEllipse } from 'components/Background/Background.styled';
import { useNavigate } from 'react-router-dom';

export interface IPlayerDashboard {
  showingChapterIdentifier?: string;
}

export const PlayerDashboard: React.FC<IPlayerDashboard> = ({ showingChapterIdentifier }) => {
  const navigate = useNavigate();
  const [selectedChapter, setSelectedChapter] = useState<IChapter | undefined>();
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const selectedChapterRef = useRef(null);
  const selectedChapterIconRef = useRef<any>(null);
  const dashboardBaseRef = useRef<any>(null);

  const allChapters = Content.getAllChapters().sort((a, b) => a.ordinal - b.ordinal);
  const urgentHelpBlockContent = Content.getUrgentHelpContent(); 
  const chatMateBlockContent = Content.getChatMateContent();

  // Animates the chapter icon, into the correct location for the chapter entry screen
  const onChapterClick = (chapter: IChapter) => {
    return async (event: any) => {
      const clickedChapterEle: HTMLDivElement = event.currentTarget;

      // the chapter that's being shown
      setIsFirstLoad(false);
      setSelectedChapter(chapter);
      
      window.history.replaceState(null, "", `/chapter/${chapter.chapterIdentifier}/intro`);

      // the above line has to wait for react to handle a state update, we'll check if it's done each frame.
      while (true) {
        if (selectedChapterRef.current && selectedChapterIconRef.current) break;

        await awaitAnimationFrame();
      }
  
      const entryContainerEle = selectedChapterRef.current as HTMLDivElement;
      const baseContainerEle = dashboardBaseRef.current as HTMLDivElement;

      const clickedIconEle: (SVGSVGElement | null) = clickedChapterEle.querySelector("svg");

      // mandatory for dealing with typescript, will never enter.
      if (!clickedChapterEle || !clickedIconEle) {
        return;
      }

      // before we animate, make sure the chapter entry is at least as tall as the div it's replacing
      const dashboardBaseRect = baseContainerEle.getBoundingClientRect();

      entryContainerEle.style.setProperty("min-height", `${dashboardBaseRect.height}px`);
      
      const selectedIconEle = selectedChapterIconRef.current;

      // Overlaying the entry screen icon ontop of the clicked button icon
      const iconOrigin = clickedIconEle.getBoundingClientRect();
      const iconTarget = selectedIconEle.getBoundingClientRect();
      
      const deltaScale = iconOrigin.width / iconTarget.width;
      const deltaX = (iconTarget.x + iconTarget.width * 0.5) - (iconOrigin.x + iconOrigin.width * 0.5);
      const deltaY = (iconTarget.y + iconTarget.height * 0.5) - (iconOrigin.y + iconOrigin.height * 0.5);

      const transformString = `translate(${-deltaX}px, ${-deltaY}px) scale(${deltaScale})`;
      selectedIconEle.style.setProperty("transform", transformString);

      selectedIconEle.style.setProperty("opacity", 1);

      // waiting a frame so the new translate location can 'bake' in
      await awaitAnimationFrame();
      
      selectedIconEle.style.setProperty("transition", `transform 400ms 200ms, filter 300ms 200ms`);

      // transition the entry screen icon into it's correct location
      selectedIconEle.style.setProperty("transform", `translate(${0}px, ${0}px) scale(${1})`);
      selectedIconEle.style.setProperty("filter", `brightness(0.7)`);
    }
  };

  const onEntryButtonClick = () =>{ 
    if (!selectedChapter) return;

    //TODO: LH: Hoped this would navigate back to the dashboard for a chapter without any exercise (the 'lean on teams' one)
    document.startViewTransition(() => {
      navigate(selectedChapter.exercise 
        ? `/chapter/${selectedChapter.chapterIdentifier}/` 
        : '/dashboard', { viewTransition: true });
    });
  };

  useEffect(() => {
    if (!showingChapterIdentifier) return;
    const showingChapter = allChapters.find(c => c.chapterIdentifier == showingChapterIdentifier);

    if (!showingChapterIdentifier) return;

    setSelectedChapter(showingChapter);
  }, []);

  const goToDash = () => {
    document.startViewTransition(() => {
      if (!selectedChapter) return;
      navigate('/dashboard', { viewTransition: true });
    });
  }

  return (
      <StyledDashboardContainer>
        <StyledDashboardBase ref={dashboardBaseRef}>
          <Background withImage={'../images/background.webp'}>
            <StyledHeaderContainer>
              <StyledSmallLogo src={`${IMAGES_URL}logo-white.png`} />
            </StyledHeaderContainer>

            <StyledHomeMessageContainer>
              <StyledWelcomeMessage>WELCOME</StyledWelcomeMessage>
              <StyledWelcomeHeading>Tools to build your<br/><b>Mental resilience</b></StyledWelcomeHeading>
            </StyledHomeMessageContainer>

            <StyledChapterContainer>
              <StyledChapters>
                {allChapters.map((chapter, index) => (
                  <StyledChapterBlockContainer key={index}>
                    <ChapterBlock title={chapter.title} onClick={onChapterClick(chapter)} time={chapter.time} image={chapter.icon} link={`/chapter/${chapter.chapterIdentifier}/`} />
                  </StyledChapterBlockContainer>
                ))}
                
                {IS_CHAT_BOT_ENABLED && chatMateBlockContent &&
                  <StyledFullBlockContainer>
                    <UrgentHelpBlock content={chatMateBlockContent} />
                  </StyledFullBlockContainer>
                }
                {urgentHelpBlockContent &&
                  <StyledFullBlockContainer>
                    <UrgentHelpBlock content={urgentHelpBlockContent} />
                  </StyledFullBlockContainer>
                }
              </StyledChapters>
            </StyledChapterContainer>
          </Background>
        </StyledDashboardBase>

        { !!selectedChapter && 
          <StyledChapterEntry ref={selectedChapterRef} $ignoreAnimation={isFirstLoad}>
            <StyledChapterEntryBackground><StyledEllipse /></StyledChapterEntryBackground>
            <StyledHeaderContainer>
              <StyledSmallLogo onClick={goToDash} src={`${IMAGES_URL}logo-white.png`} />
            </StyledHeaderContainer>
            <StyledEntryTextContainer>
              <StyledChapterEntryIcon iconRef={selectedChapterIconRef} cmsSrc={selectedChapter.icon} />
              <StyledChapterEntrySupTitle>Activity {selectedChapter.activityTextOrdinal}</StyledChapterEntrySupTitle>
              <StyledChapterEntryTitle dangerouslySetInnerHTML={ { __html: selectedChapter.introTitle || selectedChapter.title } } />
              <StyledChapterEntryText dangerouslySetInnerHTML={ { __html: selectedChapter.entryText } } />
            </StyledEntryTextContainer>

            <StyledChapterEntryButtonContainer>
              <StyledChapterEntryButton onClick={onEntryButtonClick} label={selectedChapter.exercise ? "Let's go!" : "Return home"}></StyledChapterEntryButton>
            </StyledChapterEntryButtonContainer>
          </StyledChapterEntry>
        }
      </StyledDashboardContainer>
  );
};
